/**
 * The share view's answer to "is this file binary, and how big is it?" — the two
 * facts the drive reads off the file detail (`isBinary`, `size`) to pick an editor,
 * which the share session's /info does not carry (I111).
 *
 * Asked only when `chooseShareEditor` says the choice depends on them (an
 * unregistered extension, or an editor with a `maxEditableSize`). It reads the
 * first 512 bytes of the same serve URL the viewer loads — the drive's own sniff
 * (a NUL byte in the head) — with a `Range` request, and stops reading after them
 * even when the server ignores the range.
 *
 * ONE result per serve URL, shared by every caller: the chrome's Save/Discard gate
 * and the body's dispatch both ask, and must see the same answer at the same time.
 */
import { useEffect, useSyncExternalStore } from 'react'
import { useDroppedLoadReask } from '@papan/core-ui/ui/useDroppedLoadReask'
import type { ShareFileProbe } from '@papan/collab/client'
import { RAW_READ_QUERY_PARAM, SERVED_FILE_SIZE_HEADER } from '@papan/types'
import { sessionFetch, sessionStreamSignal, currentSessionSignal, sessionEndedSince } from '@papan/api-client'

const HEAD_BYTES = 512
const MAX_ENTRIES = 64

const results = new Map<string, ShareFileProbe>()
const inFlight = new Set<string>()
const listeners = new Set<() => void>()
let version = 0

const failed = new Set<string>()

function publish(url: string, probe: ShareFileProbe): void {
  results.set(url, probe)
  while (results.size > MAX_ENTRIES) {
    const oldest = results.keys().next().value
    if (oldest === undefined) break
    results.delete(oldest)
  }
  version++
  for (const l of listeners) l()
}

function totalSize(res: Response): number | null {
  // The buffered HTML / SVG / live-content path sends no Range answer and no length,
  // only its own size header (#942).
  const own = res.headers.get(SERVED_FILE_SIZE_HEADER)
  if (own && /^\d+$/.test(own)) return Number(own)
  const range = res.headers.get('content-range')
  const m = range ? /\/(\d+)\s*$/.exec(range) : null
  if (m) return Number(m[1])
  if (res.status === 200) {
    const len = res.headers.get('content-length')
    if (len && /^\d+$/.test(len)) return Number(len)
  }
  return null
}

/**
 * Marks the probe as a probe, not a view: the serve route counts every top-level GET as
 * a view of the link (`share_links.view_count`), and a probe is not one — the server can
 * skip counting on it. A QUERY parameter, not a request header: the serve URL is on the
 * preview origin, so a custom header would make this a CORS-preflighted request the API
 * doesn't allow (`Range` alone is safelisted) and every probe would fail.
 */
export const PROBE_QUERY_PARAM = 'papanProbe'

/**
 * The URL the probe actually fetches — the serve URL's RAW read (#1095: the file's own first
 * bytes, as the drive sniffs them — never the preview's injected head) plus the probe marker.
 * The raw read is the SAVED file even on a live link (#1095 D1), so the size an editor's limit is
 * judged on is the saved size — exactly as the drive picks an editor from the saved file's detail
 * and then joins the live room. A live draft grown past the limit opens the same editor in both.
 */
export function probeUrl(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}${RAW_READ_QUERY_PARAM}=1&${PROBE_QUERY_PARAM}=1`
}

/** Exported for tests. */
export async function probeShareFile(url: string): Promise<ShareFileProbe & { failed?: true; dropped?: true }> {
  const started = currentSessionSignal()
  try {
    // #1316: bound to the share session current now — a probe answer after it ended is dropped
    const res = await sessionFetch(probeUrl(url), { credentials: 'same-origin', headers: { Range: `bytes=0-${HEAD_BYTES - 1}` }, signal: sessionStreamSignal() })   // the byte read below is a stream: a session end aborts it
    // An EMPTY file answers a range request with 416 (no byte 0) — it is text, not binary (review B5).
    if (res.status === 416) {
      void res.body?.cancel().catch(() => {})
      return { isBinary: false, size: 0 }
    }
    if (!res.ok) {
      // A refused / missing file: nothing to sniff. "Binary" sends an unregistered
      // type to the raw fallback (which shows the server's own answer); a
      // registered editor just skips the size cap and reports its own load error.
      void res.body?.cancel().catch(() => {})
      return { isBinary: true, size: null }
    }
    const size = totalSize(res)
    const head = new Uint8Array(HEAD_BYTES)
    let filled = 0
    const reader = res.body?.getReader()
    if (reader) {
      while (filled < HEAD_BYTES) {
        const { done, value } = await reader.read()
        if (done || !value) break
        const take = Math.min(value.length, HEAD_BYTES - filled)
        head.set(value.subarray(0, take), filled)
        filled += take
      }
      void reader.cancel().catch(() => {})
    }
    return { isBinary: head.subarray(0, filled).includes(0), size }
  } catch (err) {
    // #1316 review round 1: dropped because its session ended — no answer at all (the caller asks again)
    if (sessionEndedSince(started, err)) return { isBinary: true, size: null, dropped: true }
    return { isBinary: true, size: null, failed: true }
  }
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

/** `url` null = the caller doesn't need a probe (returns null). */
export function useShareFileProbe(url: string | null): ShareFileProbe | null {
  useSyncExternalStore(subscribe, () => version, () => version)
  // #1316 review round 1 (C-M1): a probe dropped at a session end publishes nothing and is asked again — never
  // "binary, failed" (the raw fallback until the next mount). Review round 2: the URL names the share session; at an end
  // that MOVES the share session it still names the ended one until the new session lands, so the re-ask waits for a
  // new URL there (`useDroppedLoadReask`) — which probes by itself.
  const { reask, begin } = useDroppedLoadReask(url)
  useEffect(() => {
    if (!url || (results.has(url) && !failed.has(url)) || inFlight.has(url)) return
    inFlight.add(url)
    const asked = begin()
    void probeShareFile(url).then((probe) => {
      inFlight.delete(url)
      if (probe.dropped) { asked.dropped(); return }
      // A network failure answers for now, but the next mount probes again (review B5 — one
      // blip used to pin the file to the raw view for the session).
      if (probe.failed) failed.add(url)
      else failed.delete(url)
      publish(url, { isBinary: probe.isBinary, size: probe.size })
    })
  }, [url, reask, begin])
  return url ? results.get(url) ?? null : null
}
